import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { LogOut, UserPlus, Users } from 'lucide-react';

import {
  createEventParticipant,
  deleteEventParticipant,
  getEventParticipants,
  getFriends,
} from '../../utils/api';
import { LoadingIndicator } from '../assets';
import EventParticipantCard from './EventParticipantCard';

const actionButton = (colorKey) => (theme) => {
  const color = theme.palette[colorKey].main;
  return {
    m: 0,
    color,
    borderColor: color,
    boxShadow: `0 4px 0 ${darken(color, 0.58)}`,
    '&:hover': { color, borderColor: color, backgroundColor: alpha(color, 0.12) },
    '&:active': { transform: 'translateY(3px)', boxShadow: 'none' },
  };
};

export default function EventParticipantsPanel({ event, onLeft }) {
  const canInvite = Boolean(event.permissions?.can_invite);
  const [participants, setParticipants] = useState(event.participants || []);
  const [friends, setFriends] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(canInvite);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!canInvite) {
      setParticipants(event.participants || []);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [participantRows, friendResponse] = await Promise.all([
        getEventParticipants({ eventId: event.id }),
        getFriends(),
      ]);
      setParticipants(participantRows);
      setFriends(friendResponse.friends || []);
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'Unable to load event participants.');
    } finally {
      setLoading(false);
    }
  }, [canInvite, event.id, event.participants]);

  useEffect(() => { load(); }, [load]);

  const activeUserIds = useMemo(
    () => new Set(participants.map((participant) => participant.user.id)),
    [participants],
  );
  const availableFriends = friends.filter((friend) => !activeUserIds.has(friend.id));

  const invite = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    setError('');
    try {
      await createEventParticipant({ event_id: event.id, user_id: Number(selectedUserId) });
      setSelectedUserId('');
      await load();
    } catch (inviteError) {
      setError(inviteError.data?.message || inviteError.message || 'Unable to invite that friend.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (participant) => {
    if (!window.confirm(`Remove ${participant.user.username} from this event?`)) return;
    setSaving(true);
    setError('');
    try {
      await deleteEventParticipant(participant.id, participant.revision);
      await load();
    } catch (removeError) {
      setError(removeError.data?.message || removeError.message || 'Unable to remove that participant.');
    } finally {
      setSaving(false);
    }
  };

  const leave = async () => {
    const participation = event.viewer_participation;
    if (!participation || !window.confirm(`Leave “${event.title}”?`)) return;
    setSaving(true);
    setError('');
    try {
      await deleteEventParticipant(participation.id, participation.revision);
      onLeft?.();
    } catch (leaveError) {
      setError(leaveError.data?.message || leaveError.message || 'Unable to leave the event.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        p: { xs: 2, sm: 2.5 },
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 18px ${alpha(theme.palette.primary.main, 0.18)}`,
      })}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Users size={20} />
          <Typography variant="h6" sx={{ color: 'primary.main' }}>People</Typography>
        </Stack>

        {error && <Alert severity="error" sx={{ color: 'secondary.main' }}>{error}</Alert>}
        {loading ? <LoadingIndicator text="loading people" /> : (
          <Stack spacing={1}>
            {participants.map((participant) => (
              <EventParticipantCard
                key={participant.id}
                participant={participant}
                saving={saving}
                onRemove={remove}
              />
            ))}
          </Stack>
        )}

        {canInvite && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField
              select
              fullWidth
              label="Invite a friend"
              value={selectedUserId}
              onChange={(inputEvent) => setSelectedUserId(inputEvent.target.value)}
              SelectProps={{ native: true }}
              sx={{
                '& .MuiInputBase-input': { color: 'primary.main' },
                '& option': { color: 'primary.main', backgroundColor: 'background.default' },
              }}
            >
              <option value="">Choose a friend</option>
              {availableFriends.map((friend) => (
                <option key={friend.id} value={friend.id}>{friend.username}</option>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<UserPlus size={17} />}
              disabled={!selectedUserId || saving}
              onClick={invite}
              sx={actionButton('primary')}
            >
              Invite
            </Button>
          </Stack>
        )}

        {event.permissions?.can_leave && (
          <Button
            variant="outlined"
            startIcon={<LogOut size={17} />}
            disabled={saving}
            onClick={leave}
            sx={actionButton('secondary')}
          >
            Leave event
          </Button>
        )}
      </Stack>
    </Box>
  );
}
